import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ApiService } from '@services/api/api.service';
import { LeadsService } from '@services/api/leads.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { Observable, map, of, switchMap, take } from 'rxjs';

const NFI_STATUS = 'No Further Interest';
const NFI_REASON_SLUG = 'lead-status-no-further-interest';
const NOTE_MAX_LENGTH = 500;

/**
 * "Move to No Further Interest" — closes a lead Remmi has flagged as no longer worth pursuing, laid
 * out as the client's mock-up
 * (REM-3537_2026-10-13_FRONTEND_CHANGES.md §3a/§4). Opened from the lead page banner and from the
 * "Close lead" button on an Action Today card, so it loads what it needs by lead id itself.
 *
 * It is the existing lead save (`PUT /update-lead`) with `not_interested` as the reason; No Further
 * Interest is not gated by the touchpoint rule, and the backend closes open follow-ups itself. The
 * optional closing note goes on top of the lead's notes as a dated line, the same
 * `DD/MM/YY: XX text` shape the lead page writes contact attempts in.
 */
@Component({
  selector: 'app-close-lead-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, DialogModule, InputTextareaModule],
  templateUrl: './close-lead-dialog.component.html',
  styleUrls: ['./close-lead-dialog.component.scss'],
})
export class CloseLeadDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  /** False renders the form alone, for a host that shows it in its own popover (the lead page banner). */
  @Input() asDialog: boolean = true;
  @Input() leadId: string = null;
  @Input() leadName: string = null;
  /**
   * Remmi's own sentence for why the lead could close (`close_suggestion.message` / banner `data.message`),
   * shown as sent: the backend owns the attempt count and the "last responded" / "latest attempt" dates
   * (REM-3537_2026-10-14 §6), so nothing here rebuilds them from raw fields.
   */
  @Input() message: string = null;
  @Input() defaultReason: string = 'Uncontactable';
  @Output() visibleChange = new EventEmitter<boolean>();
  /** The lead was saved as No Further Interest. */
  @Output() closed = new EventEmitter<string>();

  reasons: string[] = [];
  reason: string = null;
  note: string = '';
  isSaving = false;
  isSubmitted = false;
  readonly noteMaxLength = NOTE_MAX_LENGTH;

  constructor(
    private api: ApiService,
    private leadsService: LeadsService,
    private redux: ReduxService,
    private toaster: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reason = this.defaultReason;
      this.note = '';
      this.isSubmitted = false;
      this.loadReasons();
    }
  }

  /** The office's own No Further Interest reasons — the list the lead form's dropdown shows. */
  private loadReasons(): void {
    if (this.reasons.length) {
      return;
    }
    this.redux
      .getTags(this.api.getOfficeId(), false, 'lead')
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const types: any[] = response?.data?.[0]?.types || [];
          const type = types.find(item => (item?.slug || '').toLowerCase().replace('\t', '') === NFI_REASON_SLUG);
          const reasons: string[] = (type?.data || []).map(tag => `${tag?.tag_name ?? ''}`.trim()).filter(Boolean);
          reasons.sort((a, b) => a.localeCompare(b));
          // The suggested reason stays selectable even if the office has renamed or removed it.
          if (this.defaultReason && !reasons.includes(this.defaultReason)) {
            reasons.unshift(this.defaultReason);
          }
          this.reasons = reasons;
        },
        error: () => {
          this.reasons = this.defaultReason ? [this.defaultReason] : [];
        },
      });
  }

  save(): void {
    this.isSubmitted = true;
    if (!this.leadId || !this.reason || this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.buildNotes()
      .pipe(
        switchMap(reportNotes =>
          this.leadsService.updateLead(this.leadId, {
            lead_status: NFI_STATUS,
            not_interested: this.reason,
            ...(reportNotes !== null ? { report_notes: reportNotes } : {}),
          })
        )
      )
      .subscribe({
        next: (response: any) => {
          this.isSaving = false;
          if (response?.status === false) {
            this.toaster.error(response?.message || 'Could not close the lead. Please try again.');
            return;
          }
          this.toaster.success(`${this.leadName || 'Lead'} moved to ${NFI_STATUS}.`);
          this.closed.emit(this.leadId);
          this.close();
        },
        error: error => {
          this.isSaving = false;
          this.toaster.error(error?.message || 'Could not close the lead. Please try again.');
        },
      });
  }

  /**
   * The lead's notes with the closing note on top, or null when there is no note — the save then
   * leaves the notes alone. Read fresh so a line logged since the lead was opened is not dropped.
   */
  private buildNotes(): Observable<string | null> {
    const note = this.note?.trim().slice(0, NOTE_MAX_LENGTH);
    if (!note) {
      return of(null);
    }
    return this.leadsService.getLeadById(this.leadId).pipe(
      switchMap((response: any) =>
        this.redux.getUser().pipe(
          take(1),
          map((user: any) => {
            const existing = `${response?.data?.report_notes || ''}`.trim();
            const initials = this.initialsOf(user?.data ?? user);
            const line = `${moment().format('DD/MM/YY')}: ${initials ? initials + ' ' : ''}${note}`;
            return existing ? `${line}\n${existing}` : line;
          })
        )
      )
    );
  }

  private initialsOf(user: any): string {
    const first = `${user?.first_name || ''}`.trim();
    const last = `${user?.last_name || ''}`.trim();
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
